import { useSaveInsuranceMutation } from "app/store/transmission/api/transmissionTableApi";
import {
  useUpdatePreAuthStatusMutation,
  useUpdateSelfPayStatusMutation,
} from "app/store/patient/api/patientApi";
import { initialInsurance } from "app/store/transmission/slice/state/transmissionPatientModalState";
import { changeProfileField } from "app/store/transmission/slice/transmissionSlice";

import { cloneDeep } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { showErrorToast, showSuccessToast } from "utils/toast";

const useInsuranceInformation = () => {
  const {
    profile,
    guarantor,
    information,
    originalInsurance,
    patientSerialNumber,
    originalTertiaryInsurance,
    originalSecondaryInsurance,
    benchmarkInsurances,
  } = useSelector((state) => state.transmission);
  const [isUpdatingSelfPay, setIsUpdatingSelfPay] = useState(false);
  const [isUpdatingPreAuth, setIsUpdatingPreAuth] = useState(false);

  const dispatch = useDispatch();

  const [saveInsurance] = useSaveInsuranceMutation();
  const [updateSelfPayStatus] = useUpdateSelfPayStatusMutation();
  const [updatePreAuthStatus] = useUpdatePreAuthStatusMutation();

  const handleSubmitInsurance = async (type, values) => {
    const insuranceData =
      type === "insurance"
        ? { ...initialInsurance, ...values }
        : type === "secondaryInsurance"
          ? { ...initialInsurance, ...values }
          : type === "tertiaryInsurance"
            ? { ...initialInsurance, ...values }
            : null;

    const response = await saveInsurance({
      serialNumber: patientSerialNumber,
      insurance: insuranceData,
      type,
    });

    const updatedValue = cloneDeep(insuranceData);
    const insuranceType =
      type === "insurance"
        ? "originalInsurance"
        : type === "secondaryInsurance"
          ? "originalSecondaryInsurance"
          : type === "tertiaryInsurance"
            ? "originalTertiaryInsurance"
            : null;

    dispatch(
      changeProfileField({
        type: insuranceType,
        value: updatedValue,
      })
    );

    return response;
  };

  const handleUpdateSelfPayStatus = async (selfPay) => {
    setIsUpdatingSelfPay(true);
    try {
      const response = await updateSelfPayStatus({
        patientId: profile.patientId,
        selfPay,
      }).unwrap();

      dispatch(
        changeProfileField({
          type: "information",
          value: response?.data?.information,
        })
      );
      dispatch(
        changeProfileField({
          type: "originalInformation",
          value: response?.data?.information,
        })
      );
      showSuccessToast(response.message);
    } catch (error) {
      showErrorToast(error?.message || "Failed to update Self Pay status");
    } finally {
      setIsUpdatingSelfPay(false);
    }
  };

  const handleUpdatePreAuthStatus = async (preAuth) => {
    setIsUpdatingPreAuth(true);
    try {
      const response = await updatePreAuthStatus({
        patientId: profile.patientId,
        preAuth,
      }).unwrap();

      dispatch(
        changeProfileField({
          type: "information",
          value: response?.data?.information,
        })
      );
      dispatch(
        changeProfileField({
          type: "originalInformation",
          value: response?.data?.information,
        })
      );
      showSuccessToast(response.message);
    } catch (error) {
      showErrorToast(error?.message || "Failed to update Pre-Auth status");
    } finally {
      setIsUpdatingPreAuth(false);
    }
  };

  return {
    dispatch,
    handleSubmitInsurance,
    handleUpdateSelfPayStatus,
    handleUpdatePreAuthStatus,
    profile,
    guarantor,
    information,
    originalInsurance,
    benchmarkInsurances,
    originalTertiaryInsurance,
    originalSecondaryInsurance,
    isUpdatingSelfPay,
    isUpdatingPreAuth,
  };
};

export default useInsuranceInformation;
